import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, Heart, MapPin, Play, Plus, Radio, X } from "lucide-react";
import { api } from "@/lib/api";
import { PlatformBadge, imgUtil } from "./kit";
import { profileFor } from "./content";

const Bars = ({ title, rows }) => (
  <div className="cm-aud-block">
    <h5>{title}</h5>
    {rows.map(([label, val]) => (
      <div className="cm-bar" key={label}>
        <span className="cm-bar-label">{label}</span>
        <div className="cm-bar-track"><motion.i initial={{ width: 0 }} animate={{ width: `${val}%` }} transition={{ duration: 0.8, ease: [0.21, 0.5, 0.3, 1] }} /></div>
        <span className="cm-bar-val">{val}%</span>
      </div>
    ))}
  </div>
);

export default function CreatorModal({ creator, onClose }) {
  const [live, setLive] = useState({ status: "idle" });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!creator) return;
    setSaved(false); setLive({ status: "loading" });
    let active = true;
    api.get(`/social/${creator.platform}?handle=${encodeURIComponent(creator.handle)}`)
      .then((r) => { if (active) setLive({ status: r.data?.available ? "live" : "sample", data: r.data }); })
      .catch(() => { if (active) setLive({ status: "sample" }); });
    return () => { active = false; };
  }, [creator]);

  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  const p = creator ? profileFor(creator) : null;
  const liveFollowers = live.status === "live" && live.data?.followers;

  return (
    <AnimatePresence>
      {creator && (
        <motion.div className="cm-overlay" data-testid="creator-modal" onClick={onClose}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <motion.div className="cm-panel" onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, y: 40, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 30, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.21, 0.5, 0.3, 1] }}>
            <button className="cm-close" onClick={onClose} data-testid="creator-modal-close" aria-label="Close"><X size={20} /></button>

            <div className="cm-hero">
              <img src={imgUtil.wide(creator.cover)} alt="" />
              <div className="cm-hero-ov" />
              <span className={`cm-live ${live.status}`} data-testid="creator-modal-live-badge">
                <Radio size={12} /> {live.status === "loading" ? "Checking live data…" : live.status === "live" ? "LIVE DATA" : "Sample data"}
              </span>
            </div>

            <div className="cm-body">
              <div className="cm-id">
                <div className="cm-ava"><img src={imgUtil.sq(creator.avatar)} alt="" /></div>
                <div className="cm-id-text">
                  <h3>{creator.name} <BadgeCheck size={18} fill="#00F2FE" stroke="#0A0D14" /></h3>
                  <span className="cm-handle">@{creator.handle}</span>
                  <div className="cm-id-meta"><PlatformBadge platform={creator.platform} /> <span><MapPin size={13} /> {creator.location}</span> <span>{creator.niche}</span></div>
                </div>
                <div className="cm-fit"><b>{creator.fit}</b><small>AI FIT SCORE</small></div>
              </div>

              <p className="cm-bio">{p.bio}</p>

              <div className="cm-metrics">
                {p.metrics.map((m, i) => (
                  <div key={m.label} data-testid={`modal-metric-${i}`}>
                    <b>{i === 0 && liveFollowers ? (liveFollowers >= 1e6 ? `${(liveFollowers / 1e6).toFixed(1)}M` : `${Math.round(liveFollowers / 1e3)}K`) : m.value}</b>
                    <small>{m.label}{i === 0 && liveFollowers ? " · live" : ""}</small>
                  </div>
                ))}
              </div>

              <div className="cm-section">
                <h4>Audience breakdown</h4>
                <div className="cm-audience">
                  <Bars title="Age" rows={p.audience.age} />
                  <Bars title="Gender" rows={p.audience.gender} />
                  <Bars title="Top geos" rows={p.audience.geo} />
                </div>
              </div>

              <div className="cm-section">
                <h4>Recent content</h4>
                <div className="cm-reels">
                  {p.reels.map((r, i) => (
                    <div className="cm-reel" key={i} data-testid={`modal-reel-${i}`}>
                      <img src={imgUtil.wide(r.thumb)} alt="" loading="lazy" />
                      <div className="cm-reel-ov" />
                      <span className="cm-reel-play"><Play size={14} fill="#fff" stroke="none" /></span>
                      <div className="cm-reel-stats"><span><Play size={11} fill="#fff" stroke="none" /> {r.views}</span><span><Heart size={11} fill="#FF2B70" stroke="none" /> {r.likes}</span></div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="cm-actions">
                <button className={`mk-btn ${saved ? "ghost" : "ghost"}`} onClick={() => setSaved(true)} data-testid="modal-shortlist">
                  <Plus size={16} /> {saved ? "Added to shortlist" : "Add to shortlist"}
                </button>
                <Link to={`/creator/${creator.id}`} className="mk-btn ghost" data-testid="modal-view-profile">View full profile <ArrowRight size={16} /></Link>
                <Link to="/login?mode=register" className="mk-btn primary" data-testid="modal-start-campaign">Start a campaign <ArrowRight size={16} /></Link>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
